import React, { useState, useEffect } from 'react';
import { Globe, MessageSquare, Send, CheckCircle, XCircle, AlertTriangle, Loader, Link as LinkIcon, Download } from 'lucide-react';
import { sendOutreach } from '../utils/outreachApi';
import { listTemplates } from '../utils/emailTemplateApi';
import { getOrgContext } from '../utils/cloudApi';

/**
 * SendModal
 *
 * Unified outreach modal — sends ALL recipients through ChannelGate's
 * unified /outreach endpoint. ChannelGate routes each recipient based on
 * the deployment's target_channel and the recipient's preferred_channel.
 *
 * @param {{ deploymentSlug: string, targetChannel: string, recipients: Array }} sendData
 * @param {Function} onClose
 */
export function SendModal({ sendData, onClose }) {
    const [sending, setSending] = useState(false);
    const [results, setResults] = useState(null); // null = not sent yet
    const [error, setError] = useState('');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');

    const [templateError, setTemplateError] = useState('');

    // Fetch invitation templates.
    //
    // getOrgContext() returns {orgId, userId}. This read used to ask for
    // ctx.org_id, which is never set — so the lookup was skipped on every
    // send, the picker never rendered, and no template_id ever reached
    // ChannelGate. Every invitation went out as the platform default no
    // matter what Channels > Bindings > Templates held.
    useEffect(() => {
        const fetchTemplates = async () => {
            const ctx = getOrgContext();
            const orgId = ctx?.orgId;
            if (!orgId) {
                setTemplateError('No organization context — sending with the SamSurveyor default copy.');
                return;
            }
            try {
                const tpls = await listTemplates(orgId, 'invitation');
                setTemplates(tpls || []);
                // A template configured in Channels > Bindings > Templates is
                // what the sender expects
                // to go out, so preselect it rather than silently falling back
                // to the platform default copy.
                if (tpls?.length) setSelectedTemplateId(tpls[0].template_id);
            } catch (e) {
                // Never swallow this: a silent failure here is exactly what
                // made the old bug invisible.
                setTemplateError(`Could not load templates (${e.message}) — sending with the SamSurveyor default copy.`);
            }
        };
        fetchTemplates();
    }, []);

    // Reset state when modal is reopened with new sendData
    useEffect(() => {
        setResults(null);
        setError('');
        setSending(false);
    }, [sendData]);

    if (!sendData) return null;

    const { deploymentSlug, targetChannel = 'auto', recipients } = sendData;

    // Group for pre-send preview
    const browserRecipients = recipients.filter(r => r.preferred_channel === 'browser' || r.preferred_channel === 'web');
    const smsRecipients = recipients.filter(r => r.preferred_channel === 'sms');
    const hasPhone = (r) => r.phone_number && String(r.phone_number).trim().length > 0;
    const smsNoPhone = smsRecipients.filter(r => !hasPhone(r));

    // Warn about SMS recipients that may need a phone number
    const needsPhoneWarning = targetChannel !== 'browser' && smsNoPhone.length > 0;

    const handleSend = async () => {
        if (recipients.length === 0) return;
        setSending(true);
        setError('');
        try {
            const outreachRecipients = recipients.map(r => ({
                recipient_id: r.user_id,
                preferred_channel: r.preferred_channel || 'browser',
                phone_number: r.phone_number || '',
                email: r.email || '',
                name: r.name || '',
            }));
            // No selection means the sender chose "SamSurveyor default copy"
            // from the picker. Say so explicitly, because an omitted template
            // now means "use whatever Channels > Bindings > Templates holds".
            const resp = await sendOutreach(
                deploymentSlug,
                targetChannel,
                outreachRecipients,
                selectedTemplateId || undefined,
                !selectedTemplateId,
            );
            setResults(resp);
        } catch (err) {
            setError(err.message || 'Failed to send outreach');
        } finally {
            setSending(false);
        }
    };

    const handleCopyUrls = () => {
        if (!results) return;
        const urls = results.results
            .filter(r => r.url)
            .map(r => `${r.recipient_id}\t${r.url}`)
            .join('\n');
        navigator.clipboard.writeText(urls).catch(() => { });
    };

    const statusIcon = (status) => {
        if (status === 'sent') return <CheckCircle size={14} color="#22c55e" />;
        if (status === 'prepared') return <LinkIcon size={14} color="#5B9FFF" />;
        if (status === 'failed') return <XCircle size={14} color="#ef4444" />;
        return <AlertTriangle size={14} color="#f59e0b" />;
    };

    const channelLabel = (ch) => {
        if (ch === 'sms') return 'SMS';
        if (ch === 'whatsapp') return 'WhatsApp';
        if (ch === 'browser' || ch === 'web') return 'Browser';
        if (ch === 'email') return 'Email';
        if (ch === 'voice') return 'Voice';
        if (ch === 'googlechat') return 'Google Chat';
        return ch || 'auto';
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h2 style={styles.title}>
                        <Send size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
                        Send Survey
                    </h2>
                    <button style={styles.closeButton} onClick={onClose}>×</button>
                </div>

                <div style={{ fontSize: 13, color: '#5F6368', marginBottom: 16 }}>
                    Deployment: <strong>{deploymentSlug}</strong> · {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} · Channel: <strong>{channelLabel(targetChannel)}</strong>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                {/* Pre-send view */}
                {!results ? (
                    <>
                        {/* Warnings */}
                        {needsPhoneWarning && (
                            <div style={styles.warning}>
                                <AlertTriangle size={14} style={{ marginRight: 6, flexShrink: 0 }} />
                                {smsNoPhone.length} recipient{smsNoPhone.length !== 1 ? 's' : ''} prefer{smsNoPhone.length === 1 ? 's' : ''} SMS but ha{smsNoPhone.length !== 1 ? 've' : 's'} no phone number
                            </div>
                        )}

                        {/* Cross-channel info */}
                        {targetChannel !== 'auto' && (
                            <div style={styles.info}>
                                {targetChannel === 'browser' && smsRecipients.length > 0 && (
                                    <>SMS-preferred recipients will receive a text with the survey link</>
                                )}
                                {targetChannel === 'sms' && browserRecipients.length > 0 && (
                                    <>Browser-preferred recipients will be delivered via URL</>
                                )}
                            </div>
                        )}

                        {/* Template picker. Always rendered — when it was
                            hidden on an empty list, a failed lookup looked
                            identical to "no templates exist", and the send
                            quietly used the default copy instead. */}
                        <div style={styles.section}>
                            <div style={styles.sectionHeader}>Invitation Template</div>
                            <select
                                value={selectedTemplateId}
                                onChange={e => setSelectedTemplateId(e.target.value)}
                                style={{ width: '100%', padding: '6px 10px', borderRadius: 4, border: '1px solid #E8EAED', fontSize: 13 }}
                            >
                                <option value="">SamSurveyor default copy</option>
                                {templates.map(t => (
                                    <option key={t.template_id} value={t.template_id}>{t.subject || t.template_type}</option>
                                ))}
                            </select>
                            {templateError && (
                                <div style={{ fontSize: 12, color: '#E65100', marginTop: 6 }}>{templateError}</div>
                            )}
                            {!templateError && templates.length === 0 && (
                                <div style={{ fontSize: 12, color: '#5F6368', marginTop: 6 }}>
                                    No invitation templates saved — add one under Channels → Bindings → Templates.
                                </div>
                            )}
                        </div>

                        {/* Recipient list */}
                        <div style={styles.section}>
                            <div style={styles.sectionHeader}>
                                Recipients ({recipients.length})
                            </div>
                            <ul style={styles.list}>
                                {recipients.map((r, i) => (
                                    <li key={i} style={styles.listItem}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <strong>{r.email || r.name || r.user_id}</strong>
                                                {r.phone_number && (
                                                    <span style={{ fontSize: 12, color: '#5F6368', marginLeft: 8 }}>{r.phone_number}</span>
                                                )}
                                            </div>
                                            <span style={styles.channelBadge(r.preferred_channel)}>
                                                {r.preferred_channel === 'sms' ? <MessageSquare size={10} /> : <Globe size={10} />}
                                                <span style={{ marginLeft: 3 }}>{channelLabel(r.preferred_channel)}</span>
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div style={styles.actions}>
                            <button onClick={onClose} style={styles.secondaryButton}>Cancel</button>
                            <button
                                onClick={handleSend}
                                disabled={sending || recipients.length === 0}
                                style={{ ...styles.primaryButton, ...(sending ? styles.buttonDisabled : {}), marginLeft: 8 }}
                            >
                                {sending ? (
                                    <><Loader size={14} style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} /> Sending…</>
                                ) : (
                                    `Send to ${recipients.length} Recipient${recipients.length !== 1 ? 's' : ''}`
                                )}
                            </button>
                        </div>
                    </>
                ) : (
                    /* Delivery report */
                    <>
                        <div style={styles.reportSummary}>
                            {results.sent > 0 && <span style={{ color: '#22c55e' }}>✓ {results.sent} sent</span>}
                            {results.prepared > 0 && <span style={{ color: '#5B9FFF' }}>{results.sent > 0 ? ' · ' : ''}🔗 {results.prepared} prepared</span>}
                            {results.failed > 0 && <span style={{ color: '#ef4444' }}> · ✗ {results.failed} failed</span>}
                        </div>
                        <div style={styles.section}>
                            <ul style={styles.list}>
                                {results.results.map((r, i) => (
                                    <li key={i} style={styles.listItem}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {statusIcon(r.status)}
                                            <strong>{r.recipient_id}</strong>
                                            <span style={{ fontSize: 12, color: '#5F6368' }}>
                                                via {channelLabel(r.channel)}
                                                {r.notification_channel && ` (notified via ${channelLabel(r.notification_channel)})`}
                                            </span>
                                        </div>
                                        {r.url && (
                                            <div style={{ fontSize: 12, marginTop: 2 }}>
                                                <a href={r.url} target="_blank" rel="noreferrer" style={{ color: '#5B9FFF' }}>{r.url}</a>
                                            </div>
                                        )}
                                        {r.error && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 2 }}>{r.error}</div>}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div style={styles.actions}>
                            {results.results.some(r => r.url) && (
                                <button onClick={handleCopyUrls} style={{ ...styles.secondaryButton, marginRight: 8 }}>
                                    Copy URLs
                                </button>
                            )}
                            <button onClick={() => {
                                const rows = [['Recipient', 'Email', 'Name', 'Channel', 'Status', 'URL', 'Error']];
                                results.results.forEach(r => rows.push([
                                    r.recipient_id, r.email || '', r.name || '', r.channel, r.status, r.url || '', r.error || '',
                                ]));
                                const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
                                const blob = new Blob([csv], { type: 'text/csv' });
                                const a = document.createElement('a');
                                a.href = URL.createObjectURL(blob);
                                a.download = `outreach-${deploymentSlug}-${new Date().toISOString().slice(0, 10)}.csv`;
                                a.click();
                            }} style={{ ...styles.secondaryButton, marginRight: 8 }}>
                                <Download size={14} style={{ marginRight: 4 }} /> CSV
                            </button>
                            <button onClick={onClose} style={styles.secondaryButton}>Done</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        background: '#fff',
        borderRadius: 8,
        padding: 24,
        width: '100%',
        maxWidth: 560,
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    title: {
        margin: 0,
        fontSize: 18,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        fontSize: 28,
        cursor: 'pointer',
        padding: 4,
        lineHeight: 1,
        color: '#5F6368',
    },
    error: {
        backgroundColor: '#FFEBEE',
        color: '#C62828',
        padding: 12,
        borderRadius: 4,
        marginBottom: 12,
        fontSize: 13,
    },
    info: {
        backgroundColor: '#E3F2FD',
        color: '#1565C0',
        padding: 10,
        borderRadius: 4,
        marginBottom: 12,
        fontSize: 12,
    },
    warning: {
        display: 'flex',
        alignItems: 'flex-start',
        backgroundColor: '#FFF3E0',
        color: '#E65100',
        padding: 10,
        borderRadius: 4,
        marginBottom: 8,
        fontSize: 12,
    },
    section: {
        marginBottom: 16,
        padding: 12,
        border: '1px solid #E8EAED',
        borderRadius: 6,
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 600,
        fontSize: 14,
        marginBottom: 8,
        color: '#202124',
    },
    list: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    listItem: {
        padding: '6px 0',
        borderBottom: '1px solid #F1F3F4',
    },
    reportSummary: {
        padding: '8px 0',
        fontWeight: 600,
        fontSize: 14,
        marginBottom: 4,
    },
    channelBadge: (channel) => ({
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        backgroundColor: channel === 'sms' ? '#E3F2FD' : channel === 'whatsapp' ? '#E8F5E9' : channel === 'googlechat' ? '#E8F5E9' : channel === 'email' ? '#FFF3E0' : channel === 'voice' ? '#FFF8E1' : '#F3E5F5',
        color: channel === 'sms' ? '#1565C0' : channel === 'whatsapp' ? '#2E7D32' : channel === 'googlechat' ? '#1B5E20' : channel === 'email' ? '#E65100' : channel === 'voice' ? '#F57F17' : '#7B1FA2',
    }),
    primaryButton: {
        padding: '8px 20px',
        backgroundColor: '#5B9FFF',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center',
    },
    secondaryButton: {
        padding: '8px 16px',
        backgroundColor: 'var(--color-bg-base)',
        color: '#3C4043',
        border: '1px solid #DADCE0',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 14,
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 12,
    },
};
